import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  BankDetailsStatus,
  LandlordListUIModel,
  LandlordRowUIModel
} from '../../../shared/ui-models/landlord.ui-model';
import { Pagination } from '../../../shared/components/core-ui/pagination/pagination.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../shared/components/core-ui/button/button.component';
import { getLandlords, LandlordListParams } from '../../data.service';
import ImportBankDetails from './components/import-bank-details/import-bank-details.component';

type SortField = 'name' | 'bankDetailsUpdatedAt' | 'bankDetailsStatus';

const SEARCH_DEBOUNCE_MS = 400;

const COLUMNS: { key: string; label: string; sortField?: SortField }[] = [
  { key: 'name', label: 'Name', sortField: 'name' },
  { key: 'landlordId', label: 'Reference' },
  { key: 'contact', label: 'Contact' },
  { key: 'unitsCount', label: 'Units' },
  { key: 'bankDetails', label: 'Bank details', sortField: 'bankDetailsStatus' },
  { key: 'lastUpdatedAt', label: 'Last updated', sortField: 'bankDetailsUpdatedAt' }
];

function formatDate(value: string): string {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString();
}

export const LandlordsListContainer: React.FC = () => {
  const history = useHistory();

  const [list, setList] = useState<LandlordListUIModel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [bankDetailsMissing, setBankDetailsMissing] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [isImportOpen, setIsImportOpen] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const latestRequest = useRef<number>(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, bankDetailsMissing, pageSize]);

  const params: LandlordListParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search || undefined,
      bankDetailsMissing: bankDetailsMissing || undefined,
      sortBy,
      sortDirection
    }),
    [page, pageSize, search, bankDetailsMissing, sortBy, sortDirection]
  );

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setIsLoading(true);
    setHasError(false);

    getLandlords(params).then((response) => {
      if (latestRequest.current !== requestId) {
        return;
      }

      if (isErrorInfo(response)) {
        setHasError(true);
        setList(null);
      } else {
        setList(response);
      }
      setIsLoading(false);
    });
  }, [params, reloadToken]);

  const handleSort = useCallback(
    (field?: SortField) => {
      if (!field) {
        return;
      }
      if (field === sortBy) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(field);
        setSortDirection('asc');
      }
    },
    [sortBy, sortDirection]
  );

  const openLandlord = useCallback(
    (landlordId: string) => {
      history.push(`${APP_ROUTES.LANDLORD_MODULE.FULL_PATH}/${landlordId}`);
    },
    [history]
  );

  const rows: LandlordRowUIModel[] = list ? list.rows : [];

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Landlords</h1>
        <Button
          type={ButtonType.primary}
          size={ButtonSize.medium}
          onClick={() => setIsImportOpen(true)}
        >
          Import bank details
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <input
          className="w-full md:w-96 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          placeholder="Search by name, email, phone or reference"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={bankDetailsMissing}
            onChange={(event) => setBankDetailsMissing(event.target.checked)}
          />
          Bank details missing
          {list ? ` (${list.totalMissingBankDetails})` : ''}
        </label>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 font-medium ${
                    column.sortField ? 'cursor-pointer select-none' : ''
                  }`}
                  onClick={() => handleSort(column.sortField)}
                >
                  {column.label}
                  {column.sortField && sortBy === column.sortField
                    ? sortDirection === 'asc'
                      ? ' ▲'
                      : ' ▼'
                    : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              [0, 1, 2, 3, 4].map((skeletonRow) => (
                <tr key={`skeleton-${skeletonRow}`} className="border-t border-gray-100">
                  {COLUMNS.map((column) => (
                    <td key={column.key} className="px-4 py-3">
                      <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading && hasError && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-gray-600">
                  <div className="mb-3">Landlords could not be loaded</div>
                  <Button
                    type={ButtonType.default}
                    size={ButtonSize.small}
                    onClick={() => setReloadToken(reloadToken + 1)}
                  >
                    Try again
                  </Button>
                </td>
              </tr>
            )}

            {!isLoading && !hasError && rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-gray-500">
                  No landlords match this view
                </td>
              </tr>
            )}

            {!isLoading &&
              !hasError &&
              rows.map((row) => (
                <tr
                  key={row.landlordId}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                  onClick={() => openLandlord(row.landlordId)}
                >
                  <td className="px-4 py-3 font-medium text-gray-800">{row.name || '-'}</td>
                  <td className="px-4 py-3 text-gray-500">{row.landlordId}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <div>{row.email || '-'}</div>
                    <div className="text-gray-500">{row.phone || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{row.unitsCount}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        row.bankDetailsStatus === BankDetailsStatus.SET
                          ? 'rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700'
                          : 'rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700'
                      }
                    >
                      {row.bankDetailsStatus === BankDetailsStatus.SET ? 'Set' : 'Missing'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(row.lastUpdatedAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!hasError && list && list.total > 0 && (
        <Pagination
          itemsPerPage={pageSize}
          itemCounts={list.total}
          offset={page}
          handlePageChange={(pageNumber: number) => setPage(pageNumber)}
          handleItemsPerPage={(itemsNumber: number) => setPageSize(itemsNumber)}
        />
      )}

      {isImportOpen && (
        <ImportBankDetails
          config={{ showModal: isImportOpen }}
          eventHandlers={{
            closeHandler: () => setIsImportOpen(false),
            importCompletedHandler: () => setReloadToken(reloadToken + 1)
          }}
        />
      )}
    </div>
  );
};

export default LandlordsListContainer;
